import { Bell, Check, CheckCheck } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import { usePainel } from './usePainel';
import type { ViewId } from './nav-items';
import './header.css';

interface Notificacao {
  id: string; kind: string; title: string; body: string;
  target: string | null; conversationId: string | null; createdAt: string; readAt: string | null;
}
interface Resposta { items: Notificacao[]; naoLidas: number }

const quando = (iso: string) => {
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

/**
 * Alertas do operador.
 *
 * Agora vêm de `notifications`, com estado de leitura por pessoa. A versão
 * anterior derivava de falhas recentes de automação: funcionava como sinal, mas
 * a contagem caía quando o evento saía da janela recente, não quando alguém
 * olhava — então o número dizia "quantas falhas aconteceram", não "quantas
 * faltam ver", que é a única pergunta que um sino responde.
 */
export function Notificacoes({ onAbrirSecao }: { onAbrirSecao: (view: ViewId, conversationId?: string) => void }) {
  const { aberto, setAberto, raiz, gatilho } = usePainel();
  const { state, recarregar } = useResource<Resposta>('/workspace/notifications', { vazio: () => false });

  const dados = state.status === 'ready' ? state.data : { items: [], naoLidas: 0 };
  const { items, naoLidas } = dados;

  const rotulo = naoLidas === 0
    ? 'Alertas: nenhum não lido'
    : naoLidas === 1 ? 'Alertas: 1 não lido' : `Alertas: ${naoLidas} não lidos`;

  async function abrirItem(n: Notificacao) {
    try {
      if (!n.readAt) await api(`/workspace/notifications/${n.id}/read`, { method: 'POST' });
    } catch (erro) {
      // Falha em marcar como lido não pode impedir a navegação: o objetivo do
      // clique é chegar na tela do problema, não atualizar um contador.
      if (!(erro instanceof ApiError)) throw erro;
    }
    setAberto(false);
    recarregar();
    if (n.target) onAbrirSecao(n.target as ViewId, n.conversationId ?? undefined);
  }

  async function marcarTodas() {
    try {
      await api('/workspace/notifications/read-all', { method: 'POST' });
      recarregar();
    } catch { /* o contador continua como está; nada se perde */ }
  }

  return (
    <div className="painel-ancora" ref={raiz}>
      <button
        ref={gatilho}
        type="button"
        className="header-botao sino"
        aria-expanded={aberto}
        aria-haspopup="dialog"
        aria-label={rotulo}
        onClick={() => { setAberto(!aberto); if (!aberto) recarregar(); }}
      >
        <Bell size={20} aria-hidden="true" />
        {naoLidas > 0 && <span className="sino__contagem" aria-hidden="true">{naoLidas}</span>}
      </button>

      {aberto && (
        <div className="painel-flutuante" role="dialog" aria-label="Alertas">
          <div className="painel-cabeca">
            <p className="painel-titulo">Alertas</p>
            {naoLidas > 0 && (
              <button type="button" className="painel-acao" onClick={() => void marcarTodas()}>
                <CheckCheck size={14} aria-hidden="true" />
                Marcar todos como lidos
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="painel-vazio">Nenhum alerta por enquanto.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                className="alerta"
                data-lido={n.readAt !== null}
                onClick={() => void abrirItem(n)}
              >
                <span className="alerta__titulo">
                  {/* O estado de leitura não é só a cor de fundo: quem não
                      distingue tom claro precisa de outra pista. */}
                  {n.readAt ? <Check size={14} aria-hidden="true" /> : <span className="alerta__ponto" aria-hidden="true" />}
                  {n.title}
                  <span className="sr-only">{n.readAt ? ' (lido)' : ' (não lido)'}</span>
                </span>
                <span className="alerta__corpo">{n.body}</span>
                <span className="alerta__meta">{quando(n.createdAt)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
